import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import {
  ComposerPrimitive,
  useComposerRuntime,
  useThreadRuntime,
} from "@assistant-ui/react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Add01Icon,
  Attachment01Icon,
  Cancel01Icon,
  Loading03Icon,
  Mic01Icon,
  SentIcon,
  StopCircleIcon,
} from "@hugeicons/core-free-icons"
import { LiveWaveform } from "@/components/ui/live-waveform"
import { useAuth } from "@/lib/auth/use-auth"
import { refKey, useChatPanel } from "../hooks/use-chat-panel"
import { useUploadChatAttachment } from "../queries/assistant-queries"
import { useAudioRecorder } from "../hooks/use-audio-recorder"
import { useComposerDraftPersistence } from "../hooks/use-composer-draft-persistence"
import { usePageContext } from "../hooks/use-page-context"
import { AttachmentPill } from "./attachment-pill"
import { ReferenceChip } from "./reference-chip"
import { ReferencePickerPopover } from "./reference-picker-popover"
import type { ChatReference } from "../types/assistant.types"

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB
const ACCEPTED_TYPES = "image/*,application/pdf"

export function ChatComposer() {
  // All hooks must run unconditionally on every render. The read-only
  // early return below relied on threadStatus, which can flip between
  // "DRAFT" and "SUBMITTED" mid-session — moving the conditional after
  // the hook calls keeps the hook count stable.
  const composerRuntime = useComposerRuntime()
  const threadRuntime = useThreadRuntime()
  // Persist text typed-but-not-sent (and any voice transcription that
  // got appended without being sent) to localStorage. One draft slot
  // per user — there's at most one DRAFT issue per user at a time.
  // The user.id is embedded in the key so unsent text doesn't leak
  // across accounts on the same browser after logout / hijack release.
  const { user } = useAuth()
  useComposerDraftPersistence(
    user ? `composer-draft:issue:${user.id}` : null,
  )
  const pendingAttachments = useChatPanel((s) => s.pendingAttachments)
  const addAttachment = useChatPanel((s) => s.addAttachment)
  const removeAttachment = useChatPanel((s) => s.removeAttachment)
  const pendingReferences = useChatPanel((s) => s.pendingReferences)
  const addReference = useChatPanel((s) => s.addReference)
  const removeReference = useChatPanel((s) => s.removeReference)
  const threadStatus = useChatPanel((s) => s.threadStatus)
  const threadHydrated = useChatPanel((s) => s.threadHydrated)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadMut = useUploadChatAttachment()
  const pageContext = usePageContext()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerOrderId, setPickerOrderId] = useState<number | null>(null)
  const seededAutoOrderRef = useRef<number | null>(null)

  // Pre-populate a sticky "Order #N" chip when the chat opens on an
  // order page. Without this, the auto-context (already sent in
  // ``context_order_id``) is invisible to the user. The chip is
  // dismissable — once removed, we don't re-add it on the same
  // order page during this session.
  //
  // Wait for ``threadHydrated`` so we can inspect the loaded
  // conversation before adding a chip. Without that gate, every
  // panel reopen re-attaches the order even though it's already
  // referenced in earlier turns — the resulting non-empty
  // ``pendingReferences`` blocks the Confirm UI's "Confirm and file"
  // button (its pending-references guard refuses to file while there
  // are unsent references).
  useEffect(() => {
    if (!threadHydrated) return
    if (threadStatus != null && threadStatus !== "DRAFT") return
    const orderId = pageContext.orderId
    if (orderId == null) return
    if (seededAutoOrderRef.current === orderId) return

    if (pendingReferences.some((r) => r.type === "order" && r.id === orderId)) {
      // Already staged for this turn — nothing to do, but mark so we
      // don't re-evaluate every render.
      seededAutoOrderRef.current = orderId
      return
    }

    // Already user-attached in an earlier turn of this conversation?
    // The backend keeps a per-issue set of referenced order ids
    // (cf. ``referenced_order_ids`` on ``ChatDeps``), so the AI can
    // already call ``get_order(N)`` without the user re-chipping.
    // Auto-attaching every reopen also makes the Confirm UI un-
    // clickable, since its blocked-by-references guard fires whenever
    // ``pendingReferences`` is non-empty.
    const messages = threadRuntime.getState().messages ?? []
    const alreadyInHistory = messages.some((m) => {
      const parts = (m.content ?? []) as ReadonlyArray<{
        type: string
        name?: string
        data?: unknown
      }>
      return parts.some((part) => {
        if (part.type !== "data" || part.name !== "chat_reference") return false
        const ref = part.data as ChatReference | undefined
        return ref?.type === "order" && ref?.id === orderId
      })
    })
    if (alreadyInHistory) {
      seededAutoOrderRef.current = orderId
      return
    }

    seededAutoOrderRef.current = orderId
    addReference({
      type: "order",
      id: orderId,
      label: `Order #${orderId}`,
      auto: true,
    })
    // ``pendingReferences`` and ``addReference`` are intentionally
    // omitted — re-running on every reference change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageContext.orderId, threadStatus, threadHydrated])

  const handleTranscription = useCallback(
    (text: string) => {
      const current = composerRuntime.getState().text ?? ""
      const separator = current && !current.endsWith(" ") ? " " : ""
      composerRuntime.setText(current + separator + text.trim())
    },
    [composerRuntime],
  )

  const handleRecorderError = useCallback((err: Error) => {
    toast.error(err.message || "Voice input failed.")
  }, [])

  const recorder = useAudioRecorder({
    onTranscription: handleTranscription,
    onError: handleRecorderError,
  })

  const isReadOnly = threadStatus != null && threadStatus !== "DRAFT"

  if (isReadOnly) {
    return (
      <div className="border-t bg-muted/20 px-4 py-3">
        <p className="text-center text-xs text-muted-foreground">
          {threadStatus === "ABANDONED"
            ? "This conversation was discarded. Start a new chat to report another issue."
            : "This conversation was already submitted. Start a new chat to report another issue."}
        </p>
      </div>
    )
  }

  const uploadFiles = async (files: File[]) => {
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`"${file.name}" is larger than 10 MB.`)
        continue
      }
      try {
        const attachment = await uploadMut.mutateAsync(file)
        addAttachment(attachment)
      } catch {
        toast.error(`Couldn't upload "${file.name}".`)
      }
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = "" // allow re-uploading the same file
    await uploadFiles(files)
  }

  const handleReferenceSelect = (reference: ChatReference) => {
    addReference(reference)
  }

  const sendNow = () => {
    const text = (composerRuntime.getState().text ?? "").trim()
    if (!text && pendingAttachments.length === 0 && pendingReferences.length === 0) return

    // Build the user message content. Attachments ride along as
    // ``data`` parts so the registered AttachmentUI renders them inline
    // in the user bubble — no more "did the AI actually get it?" doubt.
    const content: Array<
      | { type: "text"; text: string }
      | { type: "data"; name: string; data: unknown }
    > = []
    if (text) content.push({ type: "text", text })
    for (const att of pendingAttachments) {
      content.push({
        type: "data",
        name: "attachment",
        data: {
          id: att.id,
          url: att.url,
          original_name: att.original_name,
          content_type: att.content_type,
        },
      })
    }
    for (const ref of pendingReferences) {
      content.push({
        type: "data",
        name: "chat_reference",
        data: ref,
      })
    }

    // Clear the input synchronously; the runtime adapter will see the
    // pendingAttachments via the Zustand store and POST attachment_ids
    // server-side, where the message gets bound to a ChatMessage row.
    composerRuntime.setText("")

    threadRuntime.append({
      role: "user",
      content,
      startRun: true,
    })
  }

  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // "@" opens the reference picker (Slack/Linear/Cursor convention).
    // We do NOT consume the keystroke — the user's typed "@" stays in
    // the input as ambient text, since they may go on to type "@ref".
    if (e.key === "@" && !pickerOpen) {
      setPickerOpen(true)
    }
    // Plain Enter sends; Shift+Enter is a newline. We have to intercept
    // in the *capture* phase (before bubbling) and call stopPropagation
    // so that ``ComposerPrimitive.Input`` never fires its built-in
    // ``composerRuntime.send()``. Otherwise the message goes out twice —
    // once via the composer's send and once via our ``sendNow`` — and
    // the same attachment ends up linked to two different user messages
    // on the server (visible as duplicate pills after reload).
    if (e.key !== "Enter" || e.shiftKey) return
    const text = (composerRuntime.getState().text ?? "").trim()
    if (!text && pendingAttachments.length === 0 && pendingReferences.length === 0) return
    e.preventDefault()
    e.stopPropagation()
    sendNow()
  }

  const handlePaste = async (e: React.ClipboardEvent) => {
    // Pull any file-kind items out of the clipboard. If there are none,
    // let the default text-paste behaviour through untouched.
    const items = Array.from(e.clipboardData?.items ?? [])
    const files: File[] = []
    for (const item of items) {
      if (item.kind !== "file") continue
      const file = item.getAsFile()
      if (!file) continue
      // Browsers default screenshot files to "image.png"; rename them so
      // the admin detail page shows something more identifying.
      if (file.name === "image.png" || file.name === "") {
        const ts = new Date().toISOString().replace(/[:.]/g, "-")
        const ext = file.type.split("/")[1] || "png"
        files.push(
          new File([file], `pasted-${ts}.${ext}`, { type: file.type }),
        )
      } else {
        files.push(file)
      }
    }
    if (files.length === 0) return

    e.preventDefault()
    await uploadFiles(files)
  }

  const triggerFilePicker = () => {
    fileInputRef.current?.click()
  }

  return (
    <div
      className="border-t bg-background px-3 pt-3 pb-2"
      onPaste={handlePaste}
      onKeyDownCapture={handleKeyDownCapture}
    >
      {(pendingAttachments.length > 0 || pendingReferences.length > 0) && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {pendingReferences.map((r) => (
            <ReferenceChip
              key={refKey(r)}
              reference={r}
              onRemove={() => removeReference(refKey(r))}
            />
          ))}
          {pendingAttachments.map((a) => (
            <AttachmentPill
              key={a.id}
              attachment={a}
              onRemove={() => removeAttachment(a.id)}
            />
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        hidden
        onChange={handleFileChange}
      />

      {recorder.status === "recording" || recorder.status === "transcribing" ? (
        <RecordingRow
          status={recorder.status}
          stream={recorder.stream}
          onStop={recorder.stop}
          onCancel={recorder.cancel}
        />
      ) : (
        <ComposerPrimitive.Root className="flex flex-col gap-1 rounded-xl border bg-background px-2 py-1.5 ring-1 ring-border focus-within:ring-primary/40 transition-shadow">
          <ComposerPrimitive.Input
            placeholder="Describe the issue…"
            className="min-h-8 max-h-48 w-full resize-none bg-transparent px-1 py-1 text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={triggerFilePicker}
              disabled={uploadMut.isPending}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 cursor-pointer"
              title="Attach a file"
            >
              <HugeiconsIcon
                icon={uploadMut.isPending ? Loading03Icon : Attachment01Icon}
                size={16}
                className={uploadMut.isPending ? "animate-spin" : ""}
              />
            </button>

            <ReferencePickerPopover
              open={pickerOpen}
              onOpenChange={(next) => {
                // Reset to "orders" mode whenever the picker opens. The
                // ``defaultOrderId`` prop seeds the line-items pane only
                // when set; clearing it on every open guarantees both
                // the "+" button and the "@" keystroke land on the
                // top-level orders view.
                if (next) setPickerOrderId(null)
                setPickerOpen(next)
              }}
              defaultOrderId={pickerOrderId}
              onSelect={handleReferenceSelect}
              trigger={
                // ``span`` (not ``button``) — the outer ``PopoverTrigger``
                // already renders its own ``<button>`` with the click +
                // keyboard handlers attached, so a nested ``<button>``
                // here is invalid HTML and trips React's hydration warning
                // (and base-ui complains too).
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                  title="Reference an order or line item (or type @)"
                >
                  <HugeiconsIcon icon={Add01Icon} size={16} />
                </span>
              }
            />

            {recorder.isSupported && (
              <button
                type="button"
                onClick={() => void recorder.start()}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                title="Voice input"
              >
                <HugeiconsIcon icon={Mic01Icon} size={16} />
              </button>
            )}

            <button
              type="button"
              onClick={sendNow}
              disabled={uploadMut.isPending}
              className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
              title="Send"
            >
              <HugeiconsIcon icon={SentIcon} size={16} />
            </button>
          </div>
        </ComposerPrimitive.Root>
      )}

      <p className="mt-1.5 px-1 text-[11px] text-muted-foreground">
        Enter to send · Shift + Enter for newline · @ to reference an order ·
        Paste to attach screenshots
      </p>
    </div>
  )
}

interface RecordingRowProps {
  status: "recording" | "transcribing"
  stream: MediaStream | null
  onStop: () => void
  onCancel: () => void
}

/** While recording / transcribing the composer row is replaced with a
 *  live waveform + Stop button. The Stop button becomes a Cancel
 *  ("X") while the audio is being transcribed so the user can bail
 *  out of a slow round-trip. ``LiveWaveform`` falls back to its
 *  ``processing`` idle wave during transcription so the UI doesn't
 *  go blank between mic-stop and transcript-arrived. */
function RecordingRow({ status, stream, onStop, onCancel }: RecordingRowProps) {
  const isRecording = status === "recording"
  return (
    <div className="flex items-center gap-2 rounded-xl border bg-background px-2 py-1.5 ring-1 ring-border">
      <div className="flex items-center gap-1.5 px-1">
        <span
          className={`h-2 w-2 rounded-full ${
            isRecording ? "bg-red-500 animate-pulse" : "bg-muted-foreground/60"
          }`}
        />
        <span className="text-[11px] font-medium text-muted-foreground">
          {isRecording ? "Listening…" : "Transcribing…"}
        </span>
      </div>

      <LiveWaveform
        active={isRecording}
        processing={!isRecording}
        mode="scrolling"
        height={28}
        barColor={isRecording ? "rgb(220, 38, 38)" : "rgb(115, 115, 115)"}
        className="flex-1 text-red-500"
        // Reuse the recorder's mic stream — without this the
        // visualizer would call ``getUserMedia`` itself and we'd be
        // holding two parallel streams (extra permission prompts in
        // some browsers, two "in use" device indicators).
        stream={stream}
      />

      {isRecording ? (
        <button
          type="button"
          onClick={onStop}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 cursor-pointer"
          title="Stop recording"
        >
          <HugeiconsIcon icon={StopCircleIcon} size={16} />
        </button>
      ) : (
        <button
          type="button"
          onClick={onCancel}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
          title="Cancel transcription"
        >
          <HugeiconsIcon icon={Cancel01Icon} size={16} />
        </button>
      )}
    </div>
  )
}
